// ============================================================
// TtrHand — 본인 기차 카드 + 보유 티켓 패널 (정보 비대칭, 본인만)
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §3-1 / §3-A 색상
//   SEQ=10146 — 8색 + 와일드 그리드, 본인 티켓 BFS isCompleteUx 라이브
// ============================================================
import React from 'react'
import { TtrMyHandSummary, TtrMyTicket, TtrCity } from '../store/ticket'
import TtrCardIcon from './TtrCardIcon'

const TRAIN_COLORS = ['RED', 'ORANGE', 'YELLOW', 'GREEN', 'BLUE', 'PINK', 'WHITE', 'BLACK', 'WILD'] as const

export interface TtrHandProps {
  myHand:    TtrMyHandSummary
  myTickets: TtrMyTicket[]
  cities:    TtrCity[]
  /** 티켓 클릭 시 보드 도시 강조 토글 */
  onTicketClick?: (ticketId: number) => void
  highlightedTicketId?: number | null
  /** SEQ=10211 STEP=6 — 모든 티켓 도시 보기 토글 상태 (PC + 모바일 핸드 탭 공통). */
  showAllTicketCities?: boolean
  onToggleAllTicketCities?: () => void
}

const TtrHand: React.FC<TtrHandProps> = ({
  myHand, myTickets, cities, onTicketClick, highlightedTicketId,
  showAllTicketCities, onToggleAllTicketCities,
}) => {
  const cityName = (id: number): string =>
    cities.find(c => c.cityId === id)?.name ?? `#${id}`

  return (
    <section className="ttr-hand-panel">
      <h3>내 카드</h3>
      <ul className="ttr-card-grid">
        {TRAIN_COLORS.map(c => (
          <li
            key={c}
            className={'ttr-card-cell ttr-color-' + c.toLowerCase()}
            title={`${c} ${myHand[c] ?? 0}장`}
          >
            <TtrCardIcon color={c} />
            <span className="ttr-card-label">{c}</span>
            <span className="ttr-card-count">{myHand[c] ?? 0}</span>
          </li>
        ))}
      </ul>

      <div className="ttr-hand-ticket-header">
        <h3>내 티켓 ({myTickets.length}장)</h3>
        {/* SEQ=10211 STEP=6: 모든 본인 티켓의 도시 합집합을 보드에 강조 토글 */}
        {onToggleAllTicketCities && (
          <button
            type="button"
            className={'ttr-show-all-cities-btn' + (showAllTicketCities ? ' active' : '')}
            onClick={onToggleAllTicketCities}
            disabled={myTickets.length === 0}
            title={showAllTicketCities ? '전체 도시 강조 끄기' : '본인 티켓 전체 도시 보드에 표시'}
          >
            {showAllTicketCities ? '📍 전체 끄기' : '📍 전체 도시'}
          </button>
        )}
      </div>
      <ul className="ttr-my-ticket-list">
        {myTickets.length === 0 && <li className="ttr-empty">티켓 없음</li>}
        {myTickets.map(t => (
          <li
            key={t.ticketId}
            className={
              'ttr-my-ticket-row' +
              (t.isCompleteUx ? ' complete' : ' incomplete') +
              (highlightedTicketId === t.ticketId ? ' highlighted' : '')
            }
            onClick={() => onTicketClick && onTicketClick(t.ticketId)}
            title={t.isCompleteUx ? '연결됨 (UX, 종료 시 권위 확정)' : '미연결'}
          >
            <span className="ttr-ticket-points">{t.points}</span>
            <span className="ttr-ticket-cities">
              {cityName(t.cityAId)} ↔ {cityName(t.cityBId)}
            </span>
            <span className={'ttr-ticket-status ' + (t.isCompleteUx ? 'ok' : 'ng')}>
              {t.isCompleteUx ? 'OK' : 'X'}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default TtrHand
